import React, { useState, useEffect } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Upload } from '@appkit4/react-components/upload';
import { Button } from '@appkit4/react-components/button';
import { Table, Column } from '@appkit4/react-components/table';
import { Checkbox } from '@appkit4/react-components/checkbox';
import { Accordion, AccordionItem } from '@appkit4/react-components/accordion';
import { useFileUpload } from '../../../helpers/useFileUpload';
import useToast from '../../../helpers/useToast';
import './UploadModal.scss';
import UploadingDocumentsModal from '../uploadingDocuments/UploadingDocumentsModal';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';

import {
    usePostV1FillOnlineFormGenerateUploadExcelTemplate,
    usePostV1FillPdfGenerateUploadExcelTemplate,
} from '../../../api/generated/endpoint';
import {
    GenerateExcelTemplateRequest,
    GenerateExcelTemplateResponse,
    DownloadExampleExportTemplateRequest,
} from '../../../api/generated/models';

interface UploadModalProps {
    visible: boolean;
    onClose: () => void;
    onUpload?: (files: File[]) => void;
    processId?: any;
    processTypeId?: any;
    preloadedValidationErrors?: string[];
    preloadedValidationWarnings?: string[];
    preloadedOriginalFile?: File | null;
    documentTypeIds?: number[];
}

const UploadModal: React.FC<UploadModalProps> = ({
    visible,
    onClose,
    onUpload,
    processId,
    processTypeId,
    preloadedValidationErrors,
    preloadedValidationWarnings,
    preloadedOriginalFile,
    documentTypeIds,
}) => {
    const generateExcelTemplateMutation =
        usePostV1FillOnlineFormGenerateUploadExcelTemplate();
    const generateFillPdfTemplateMutation =
        usePostV1FillPdfGenerateUploadExcelTemplate();

    const handleDownloadOnlineFormExample = () => {
        const docTypeIds =
            documentTypeIds && documentTypeIds.length > 0
                ? documentTypeIds
                : [];
        const requestData: GenerateExcelTemplateRequest = {
            documentTypeIds: docTypeIds,
        };
        generateExcelTemplateMutation.mutate(
            { data: requestData },
            {
                onSuccess: (response: GenerateExcelTemplateResponse) => {
                    if (response && response.onlineFormTemplateSASUrl) {
                        const downloadLink = document.createElement('a');
                        downloadLink.href = response.onlineFormTemplateSASUrl;
                        downloadLink.target = '_blank';
                        downloadLink.download = 'example_template.xlsx';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        showToast(
                            'Success',
                            'Example template downloaded successfully',
                            'success'
                        );
                    } else {
                        showToast(
                            'Error',
                            'Failed to download example template. No URL returned.',
                            'error'
                        );
                    }
                },
                onError: (error) => {
                    console.error('Error downloading example template:', error);
                    showToast(
                        'Error',
                        'Failed to download example template. Please try again.',
                        'error'
                    );
                },
            }
        );
    };

    const handleDownloadFillPdfExample = () => {
        const docTypeIds =
            documentTypeIds && documentTypeIds.length > 0
                ? documentTypeIds
                : [];
        const requestData: DownloadExampleExportTemplateRequest = {
            documentTypeIds: docTypeIds,
        };
        generateFillPdfTemplateMutation.mutate(
            { data: requestData },
            {
                onSuccess: (response: any) => {
                    if (response && response.uploadTemplateSasLink) {
                        const downloadLink = document.createElement('a');
                        downloadLink.href = response.uploadTemplateSasLink;
                        downloadLink.target = '_blank';
                        downloadLink.download = 'fill_pdf_template.xlsx';
                        document.body.appendChild(downloadLink);
                        downloadLink.click();
                        document.body.removeChild(downloadLink);
                        showToast(
                            'Success',
                            'Example template downloaded successfully',
                            'success'
                        );
                    } else {
                        showToast(
                            'Error',
                            'Failed to download example template. No URL returned.',
                            'error'
                        );
                    }
                },
                onError: (error) => {
                    console.error('Error downloading example template:', error);
                    showToast(
                        'Error',
                        'Failed to download example template. Please try again.',
                        'error'
                    );
                },
            }
        );
    };

    const [files, setFiles] = useState<any>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [fileString, setFileString] = useState([]);
    const { uploadFiles, uploadProgress } = useFileUpload();
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [isRetryUploading, setIsRetryUploading] = useState(false);
    const [skipUnnecessaryFieldValidation, setSkipUnnecessaryFieldValidation] =
        useState(false);
    const [originalUploadedFile, setOriginalUploadedFile] =
        useState<File | null>(null);

    useEffect(() => {
        if (
            visible &&
            preloadedValidationErrors &&
            preloadedValidationErrors.length > 0
        ) {
            setValidationErrors(preloadedValidationErrors);
            setShowValidationErrors(true);
        }
    }, [visible, preloadedValidationErrors]);

    useEffect(() => {
        if (
            visible &&
            preloadedValidationWarnings &&
            preloadedValidationWarnings.length > 0
        ) {
            setValidationWarnings(preloadedValidationWarnings);
            setShowValidationErrors(true);
        }
    }, [visible, preloadedValidationWarnings]);

    useEffect(() => {
        if (visible && preloadedOriginalFile && !originalUploadedFile) {
            setOriginalUploadedFile(preloadedOriginalFile);
        }
    }, [visible, preloadedOriginalFile, originalUploadedFile]);

    const showToast = useToast();
    const auth = useAuth();

    const handleFileChange = (file: File, fileList: FileList): void => {
        // console.log('onChange', file, fileList);
        setFiles(fileList);
    };

    const handleUpload = async () => {
        if (files?.length > 0) {
            const steps = files.map(
                (_: any, index: number) => `File ${index + 1}`
            );
            setFileString(steps);
            if (!processId) {
                showToast(
                    'Error',
                    'Process ID is required for upload',
                    'error'
                );
                return;
            }

            setIsUploading(true);
            setShowValidationErrors(false);
            setValidationErrors([]);
            setValidationWarnings([]);
            try {
                if (processTypeId === 3) {
                    await handleOnlineFormUpload(
                        files[0],
                        !skipUnnecessaryFieldValidation
                    );
                } else if (processTypeId === 2) {
                    await handleFillPdfUpload(files[0]);
                } else {
                    setShowProgressModal(true);
                    await uploadFiles(files, processId);
                    setShowProgressModal(false);
                }
                onUpload?.(files);
                showToast('Success', 'Files uploaded successfully', 'success');
                setFiles([]);
                onClose();
            } catch (error) {
                console.error('Upload failed:', error);
                setShowProgressModal(false);
            } finally {
                setIsUploading(false);
                setShowProgressModal(false);
            }
        }
    };

    const handleOnlineFormUpload = async (
        file: any,
        validateUnnecessaryFields: boolean = true
    ) => {
        try {
            setOriginalUploadedFile(file.originFile || file);
            const formData = new FormData();
            formData.append('ProcessId', processId);
            formData.append('File', file.originFile || file);
            formData.append(
                'ValidateUnnecessaryFields',
                validateUnnecessaryFields.toString()
            );

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            await axios.post('/v1/FillOnlineForm/UploadExcel', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                baseURL: process.env.REACT_APP_API_BASE_URL || '',
            });
        } catch (error: any) {
            if (error?.response?.data) {
                const responseData = error.response.data;
                // console.log('responseData', responseData);
                if (responseData.errors && Array.isArray(responseData.errors)) {
                    setValidationErrors(responseData.errors);
                }
                if (
                    responseData.warnings &&
                    Array.isArray(responseData.warnings)
                ) {
                    setValidationWarnings(responseData.warnings);
                }
                if (
                    !responseData.errors &&
                    !responseData.warnings &&
                    Array.isArray(responseData)
                ) {
                    setValidationErrors(responseData);
                }
            } else {
                setValidationErrors(['Unknown validation error occurred']);
            }
            setShowValidationErrors(true);
            setFiles([]);
            throw error;
        }
    };

    const handleFillPdfUpload = async (file: any) => {
        try {
            setOriginalUploadedFile(file.originFile || file);
            const formData = new FormData();
            formData.append('ProcessId', processId);
            formData.append('File', file.originFile || file);

            const token = auth.user?.id_token;

            if (!token) {
                throw new Error('No ID token available');
            }

            await axios.post('/v1/FillPdf/UploadExcel', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                baseURL: process.env.REACT_APP_API_BASE_URL || '',
            });
        } catch (error: any) {
            const responseData = error?.response?.data;
            const errors = Array.isArray(responseData)
                ? responseData
                : [responseData?.toString() || 'Upload failed'];

            setValidationErrors(errors);
            setShowValidationErrors(true);
            setFiles([]);
            throw error;
        }
    };

    const handleRetryUpload = async () => {
        if (files?.length > 0) {
            setIsRetryUploading(true);
            setShowValidationErrors(false);
            setValidationErrors([]);
            setValidationWarnings([]);
            try {
                if (processTypeId === 3) {
                    await handleOnlineFormUpload(
                        files[0],
                        !skipUnnecessaryFieldValidation
                    );
                } else if (processTypeId === 2) {
                    await handleFillPdfUpload(files[0]);
                }
                onUpload?.(files);
                showToast('Success', 'File uploaded successfully', 'success');
                setFiles([]);
                onClose();
            } catch (error) {
                console.error('Retry upload failed:', error);
            } finally {
                setIsRetryUploading(false);
            }
        }
    };

    const handleProceedWithWarnings = async () => {
        if (originalUploadedFile) {
            setIsRetryUploading(true);
            setShowValidationErrors(false);
            setValidationErrors([]);
            setValidationWarnings([]);
            try {
                await handleOnlineFormUpload(originalUploadedFile, false);
                onUpload?.([originalUploadedFile]);
                showToast(
                    'Success',
                    'File uploaded successfully with warnings',
                    'success'
                );
                setFiles([]);
                setOriginalUploadedFile(null);
                onClose();
            } catch (error) {
                console.error('Proceed with warnings failed:', error);
            } finally {
                setIsRetryUploading(false);
            }
        }
    };

    const parseError = (error: string) => {
        const oldFormatMatch = error.match(/Field '([^']+)' \((\d+)\) (.+)/);
        if (oldFormatMatch) {
            return {
                fieldName: oldFormatMatch[1],
                fieldId: oldFormatMatch[2],
                errorMessage: oldFormatMatch[3],
            };
        }

        const newFormatMatch = error.match(/Field '([^']+)': (.+)/);
        if (newFormatMatch) {
            return {
                fieldName: newFormatMatch[1],
                fieldId: '',
                errorMessage: newFormatMatch[2],
            };
        }

        // Fallback for unformatted messages
        return {
            fieldName: '',
            fieldId: '',
            errorMessage: error,
        };
    };

    const errorData = validationErrors.map((error, index) => ({
        id: index,
        ...parseError(error),
    }));

    const handleClose = () => {
        setFiles([]);
        setOriginalUploadedFile(null);
        setSkipUnnecessaryFieldValidation(false);
        setValidationErrors([]);
        setValidationWarnings([]);
        setShowValidationErrors(false);
        onClose();
    };

    const downloadValidationErrorsCSV = () => {
        if (validationErrors.length === 0) return;

        const csvContent = [
            'Field Name,Field ID,Error Message',
            ...errorData.map(
                (error) =>
                    `"${error.fieldName}","${error.fieldId}","${error.errorMessage}"`
            ),
        ].join('\n');

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
            'download',
            `validation_errors_${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <>
            <UploadingDocumentsModal
                visible={showProgressModal}
                fileString={fileString}
                uploadProgress={uploadProgress}
                files={files}
            ></UploadingDocumentsModal>

            <Modal
                visible={visible}
                title="Upload Documents"
                onCancel={handleClose}
                footer={
                    <div className="upload-modal-footer">
                        {showValidationErrors &&
                        (processTypeId === 3 || processTypeId === 2) &&
                        (validationErrors.length > 0 ||
                            validationWarnings.length > 0) ? (
                            <div className="validation-buttons">
                                <Button
                                    variant="primary"
                                    onClick={handleRetryUpload}
                                    disabled={
                                        files?.length === 0 || isRetryUploading
                                    }
                                    loading={isRetryUploading}
                                    tabIndex={0}
                                >
                                    {isRetryUploading
                                        ? 'Uploading...'
                                        : 'Retry Upload'}
                                </Button>
                                {validationErrors.length === 0 &&
                                    validationWarnings.length > 0 && (
                                        <Button
                                            variant="secondary"
                                            onClick={handleProceedWithWarnings}
                                            disabled={isRetryUploading}
                                            tabIndex={0}
                                        >
                                            Proceed with warnings
                                        </Button>
                                    )}
                            </div>
                        ) : (
                            <Button
                                variant="primary"
                                onClick={handleUpload}
                                disabled={
                                    files?.length === 0 ||
                                    isUploading ||
                                    isRetryUploading
                                }
                                loading={isUploading || isRetryUploading}
                                tabIndex={0}
                            >
                                {isUploading || isRetryUploading
                                    ? 'Uploading...'
                                    : 'Upload Files'}
                            </Button>
                        )}
                    </div>
                }
                closable={true}
                className="upload-modal"
                modalStyle={{
                    width:
                        showValidationErrors &&
                        (processTypeId === 3 || processTypeId === 2)
                            ? '60rem'
                            : '33.75rem',
                }}
            >
                <div className="upload-modal-content">
                    {showValidationErrors &&
                    (processTypeId === 3 || processTypeId === 2) &&
                    (validationErrors.length > 0 ||
                        validationWarnings.length > 0) ? (
                        <div className="validation-errors-section">
                            {validationErrors.length > 0 && (
                                <>
                                    <p className="error-description">
                                        The Excel file could not be processed
                                        due to the following validation errors:
                                    </p>
                                    <div className="validation-errors-table">
                                        <Table
                                            originalData={errorData}
                                            pageSize={10}
                                            hasTitle={true}
                                            className="validation-errors-table-content"
                                        >
                                            <Column
                                                field="fieldName"
                                                className="field-name-column"
                                            >
                                                Field Name
                                            </Column>
                                            <Column
                                                field="fieldId"
                                                className="field-id-column"
                                            >
                                                Field ID
                                            </Column>
                                            <Column
                                                field="errorMessage"
                                                className="error-message-column"
                                            >
                                                Error Message
                                            </Column>
                                        </Table>
                                    </div>
                                    <div className="download-csv-section">
                                        <Button
                                            variant="secondary"
                                            icon="icon-download-outline"
                                            onClick={
                                                downloadValidationErrorsCSV
                                            }
                                            disabled={
                                                validationErrors.length === 0
                                            }
                                        >
                                            Download Validation errors
                                        </Button>
                                    </div>
                                </>
                            )}
                            {validationWarnings.length > 0 && (
                                <div className="validation-warnings-section">
                                    <Accordion>
                                        <AccordionItem
                                            itemKey="warnings"
                                            className="warning-accordion"
                                            templateHeader={() => (
                                                <div className="warning-header">
                                                    <span className="Appkit4-icon icon-alert-outline warning-icon"></span>
                                                    <span className="warning-title">
                                                        You have{' '}
                                                        {
                                                            validationWarnings.length
                                                        }{' '}
                                                        warning
                                                        {validationWarnings.length >
                                                        1
                                                            ? 's'
                                                            : ''}
                                                    </span>
                                                </div>
                                            )}
                                        >
                                            <div className="warning-content">
                                                <p className="warning-description">
                                                    These warnings can be
                                                    disregarded. They indicate
                                                    fields with values that are
                                                    not currently required based
                                                    on your selections.
                                                </p>
                                                <div className="warning-list-container">
                                                    {validationWarnings.map(
                                                        (warning, index) => (
                                                            <div
                                                                key={index}
                                                                className="warning-item"
                                                            >
                                                                <div className="warning-item-content">
                                                                    <span className="warning-bullet">
                                                                        •
                                                                    </span>
                                                                    <span>
                                                                        {
                                                                            warning
                                                                        }
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </AccordionItem>
                                    </Accordion>
                                </div>
                            )}

                            <div className="retry-upload-section">
                                {validationErrors.length > 0 && (
                                    <div className="validation-checkbox-section">
                                        <Checkbox
                                            checked={
                                                skipUnnecessaryFieldValidation
                                            }
                                            onChange={(checked: any) =>
                                                setSkipUnnecessaryFieldValidation(
                                                    checked
                                                )
                                            }
                                        >
                                            Skip validation for fields that are
                                            not currently required
                                        </Checkbox>
                                        <p className="checkbox-caption">
                                            When enabled, fields that
                                            shouldn&apos;t be filled based on
                                            conditions will be ignored during
                                            validation. Their values will be
                                            preserved and used if those fields
                                            become required later.
                                        </p>
                                    </div>
                                )}
                                <Upload
                                    onChange={handleFileChange}
                                    multiple={false}
                                    autoUpload={false}
                                    acceptFileType=".xlsx,.xls"
                                    uploadInstruction="Upload a corrected Excel file"
                                    uploadTitle="Upload Excel File"
                                    maxFileSize={10 * 1024 * 1024}
                                    config={{
                                        trigger: false,
                                        type: 'inline',
                                        size: true,
                                    }}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            {processTypeId === 3 ? (
                                <>
                                    <div
                                        className="download-example-container"
                                        style={{ marginBottom: '1rem' }}
                                    >
                                        <Button
                                            className="download-example-button"
                                            icon="icon-download-outline"
                                            onClick={
                                                handleDownloadOnlineFormExample
                                            }
                                            size="small"
                                            disabled={
                                                generateExcelTemplateMutation.isLoading
                                            }
                                        >
                                            {generateExcelTemplateMutation.isLoading
                                                ? 'Downloading...'
                                                : 'Download example template'}
                                        </Button>
                                    </div>
                                    <Upload
                                        onChange={handleFileChange}
                                        multiple={false}
                                        autoUpload={false}
                                        acceptFileType=".xlsx,.xls"
                                        uploadInstruction="You can upload excel files (.xlsx, .xls). The max file size is 10mb."
                                        uploadTitle="Upload your file"
                                        maxFileSize={10 * 1024 * 1024}
                                        config={{
                                            trigger: false,
                                            type: 'inline',
                                            size: true,
                                        }}
                                    />
                                    <div className="validation-checkbox-section">
                                        <Checkbox
                                            checked={
                                                skipUnnecessaryFieldValidation
                                            }
                                            onChange={(checked: any) =>
                                                setSkipUnnecessaryFieldValidation(
                                                    checked
                                                )
                                            }
                                        >
                                            Skip validation for fields that are
                                            not currently required
                                        </Checkbox>
                                        <p className="checkbox-caption">
                                            When enabled, fields that
                                            shouldn&apos;t be filled based on
                                            conditions will be ignored during
                                            validation. Their values will be
                                            preserved and used if those fields
                                            become required later.
                                        </p>
                                    </div>
                                </>
                            ) : processTypeId === 2 ? (
                                <>
                                    <div
                                        className="download-example-container"
                                        style={{ marginBottom: '1rem' }}
                                    >
                                        <Button
                                            className="download-example-button"
                                            icon="icon-download-outline"
                                            onClick={
                                                handleDownloadFillPdfExample
                                            }
                                            size="small"
                                            disabled={
                                                generateFillPdfTemplateMutation.isLoading
                                            }
                                        >
                                            {generateFillPdfTemplateMutation.isLoading
                                                ? 'Downloading...'
                                                : 'Download example template'}
                                        </Button>
                                    </div>
                                    <Upload
                                        onChange={handleFileChange}
                                        multiple={false}
                                        autoUpload={false}
                                        acceptFileType=".xlsx,.xls"
                                        uploadInstruction="You can upload excel files (.xlsx, .xls). The max file size is 10mb."
                                        uploadTitle="Upload your file"
                                        maxFileSize={10 * 1024 * 1024}
                                        config={{
                                            trigger: false,
                                            type: 'inline',
                                            size: true,
                                        }}
                                    />
                                </>
                            ) : (
                                <Upload
                                    onChange={handleFileChange}
                                    multiple={true}
                                    autoUpload={false}
                                    acceptFileType=".JPG,.PNG,.PDF"
                                    uploadInstruction="You can upload JPG, PNG or PDF files. The max file size is 499mb."
                                    uploadTitle="Upload your files"
                                    maxFileSize={499 * 1024 * 1024}
                                    config={{
                                        trigger: false,
                                        type: 'inline',
                                        size: true,
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default UploadModal;
